


















import Vue, { PropType } from 'vue';
import {
  Breakpoint,
  getCurrentBreakpoint,
} from '@/utils/breakpoints';
import imgPurple from '@/assets/images/speaker_with_gradient_purple.png';
import imgGreen from '@/assets/images/speaker_with_gradient_green.png';
import imgDefault from '@/assets/images/speaker_with_gradient.png';

export enum Position {
  Default = 'default',
  UpperLeft = 'upper-left',
  UpperRight = 'upper-right',
  BottomLeft = 'bottom-left',
  BottomRight = 'bottom-right',
  LeftSide = 'left-side',
  RightSide = 'right-side',
  TopSide = 'top-side',
  BottomSide = 'bottom-side'
}

export enum Variant {
  Default = 'default',
  Green = 'green',
  Purple = 'purple'
}

export default Vue.extend({
  name: 'SpeakerWithGradient',
  props: {
    svgClass: String,
    position: {
      type: String as PropType<Position>,
      default: () => Position.Default.toString(),
      validator: (value: string): boolean => [
        Position.Default.toString(),
        Position.UpperLeft.toString(),
        Position.UpperRight.toString(),
        Position.BottomLeft.toString(),
        Position.BottomRight.toString(),
        Position.LeftSide.toString(),
        Position.RightSide.toString(),
        Position.TopSide.toString(),
        Position.BottomSide.toString(),
      ].indexOf(value) !== -1,
    },
    variant: {
      type: String as PropType<Variant>,
      default: () => Variant.Default.toString(),
      validator: (value: string): boolean => [
        Variant.Default.toString(),
        Variant.Green.toString(),
        Variant.Purple.toString(),
      ].indexOf(value) !== -1,
    },
  },
  computed: {
    imgSrc() {
      switch (this.variant) {
      case Variant.Purple.toString():
        return imgPurple;
      case Variant.Green.toString():
        return imgGreen;
      default:
        return imgDefault;
      }
    },
  },
  data() {
    return {
      containerSize: {
        width: this.$windowWidth,
        height: this.$windowWidth,
      },
      imgSize: {
        width: this.$windowWidth,
        height: this.$windowWidth,
      },
      imgOffset: {
        top: 0,
        left: 0,
      },
    };
  },
  created() {
    // Calculate the initial image size and offset when the component is created
    this.calculateImageSize();
    this.calculateImageOffset();
  },
  watch: {
    // React to changes in `this.$windowWidth`, which occurrs when the screen is
    // resized horizontally. Re-calculate the size of the background image.
    $windowWidth() {
      this.calculateImageSize();
    },

    // React to changes in `this.imgSize`, which occurrs when the background image has been resized
    // horizontally during `this.calculateImageSize()`. Re-calculate the offset of the background image.
    imgSize() {
      this.calculateImageOffset();
    },
  },
  methods: {
    calculateImageSize() {
      const breakpoint = getCurrentBreakpoint(this.$windowWidth);

      // If we use the full window width, we get a horizontal scrollbar because the full window width
      // does not take into account the vertical scrollbar. Because we don't want a horizontal scrollbar,
      // we make sure to subtract some pixels from the full width so that the image width is staying
      // inside the vertical scrollbar.
      // On mobile screens there is no vertical scrollbar so ignore this there.
      const scrollbarWidthInPixels = breakpoint === Breakpoint.SM
        ? 0
        : 20; // TODO: Get actual scrollbar width from javascript/browser/DOM

      // Equals the actual image width of @/assets/images/speaker_with_gradient.png.
      // If the image file is updated, this may need to be updated also.
      const originalImageWidth = 1580;

      // Actual available width is the full window width minus some pixels for vertical scrollbar
      let imgWidth = this.$windowWidth - scrollbarWidthInPixels;

      // Don't stretch the image larger than its original size
      if (imgWidth > originalImageWidth) {
        imgWidth = originalImageWidth;
      }

      // The original image ratio is pretty much completely square
      // so just set the new height equal to the new width to maintain the ratio.
      const imgHeight = imgWidth;

      // console.log(`height: ${imgHeight}, width: ${imgWidth}`);

      this.imgSize = {
        width: imgWidth,
        height: imgHeight,
      };

      this.containerSize = {
        width: this.$windowWidth - scrollbarWidthInPixels,
        height: imgHeight,
      };
    },
    calculateImageOffset() {
      let top = 0;
      let left = 0;

      switch (this.position) {
      case Position.UpperLeft.toString():
        top = -(this.imgSize.height / 2);
        left = -(this.imgSize.width / 2);
        break;
      case Position.UpperRight.toString():
        top = -(this.imgSize.height / 2);
        left = this.imgSize.width / 2;
        break;
      case Position.BottomLeft.toString():
        top = this.imgSize.height / 2;
        left = -(this.imgSize.width / 2);
        break;
      case Position.BottomRight.toString():
        top = this.imgSize.height / 2;
        left = this.imgSize.width / 2;
        break;
      case Position.LeftSide.toString():
        top = 0;
        left = -(this.imgSize.width / 2);
        break;
      case Position.RightSide.toString():
        top = 0;
        left = this.imgSize.width / 2;
        break;
      case Position.TopSide.toString():
        top = -(this.imgSize.height / 2);
        left = 0;
        break;
      case Position.BottomSide.toString():
        top = this.imgSize.height / 2;
        left = 0;
        break;
      default:
        top = 0;
        left = 0;
        break;
      }

      // console.log(`top: ${top}, left: ${left}`);

      this.imgOffset = {
        top,
        left,
      };
    },
  },
});
